import { useEffect, useRef } from 'react';
import { compileFn } from '@/lib/bytebeatCompiler';

interface WaveformVisualizerProps {
  code: string;
  args: Record<string, number>;
  isPlaying: boolean;
  className?: string;
}

export function WaveformVisualizer({ code, args, isPlaying, className }: WaveformVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number>(0);
  const tRef = useRef<number>(0);
  const fnRef = useRef<((t: number, args: Record<string, number>) => number) | null>(null);

  // Compile function when code or args change
  useEffect(() => {
    fnRef.current = compileFn(code, Object.keys(args));
  }, [code, args]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let running = true;
    let lastTime = 0;

    function draw(timestamp: number) {
      if (!running) return;
      if (!canvas || !ctx) return;

      const dt = timestamp - lastTime;
      lastTime = timestamp;

      const w = canvas.width;
      const h = canvas.height;

      // Scroll-and-draw for a moving waveform effect
      if (isPlaying) {
        tRef.current += (dt / 1000) * 8000; // advance at ~8kHz visual rate
      }

      ctx.clearRect(0, 0, w, h);

      // Background
      ctx.fillStyle = 'rgba(15, 15, 26, 0.95)';
      ctx.fillRect(0, 0, w, h);

      // Grid lines
      ctx.strokeStyle = 'rgba(0, 230, 255, 0.06)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 4; i++) {
        const y = (h / 4) * i;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      const fn = fnRef.current;
      const argVals = args;

      // Waveform
      ctx.beginPath();
      ctx.strokeStyle = isPlaying ? 'hsl(195, 100%, 60%)' : 'hsl(195, 60%, 40%)';
      ctx.lineWidth = 1.5;
      ctx.shadowColor = isPlaying ? 'hsl(195, 100%, 60%)' : 'transparent';
      ctx.shadowBlur = isPlaying ? 6 : 0;

      const samples = w;
      const tBase = Math.floor(tRef.current);

      for (let x = 0; x < samples; x++) {
        const t = tBase + x * 8;
        let val = 128;
        if (fn) {
          try {
            val = fn(t, argVals);
            if (!isFinite(val)) val = 128;
          } catch {
            val = 128;
          }
        } else {
          // Show a flat line if no valid code
          val = 128;
        }
        const y = h - ((val / 255) * h);
        if (x === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      frameRef.current = requestAnimationFrame(draw);
    }

    frameRef.current = requestAnimationFrame(draw);

    return () => {
      running = false;
      cancelAnimationFrame(frameRef.current);
    };
  }, [code, args, isPlaying]);

  return (
    <canvas
      ref={canvasRef}
      width={600}
      height={120}
      className={className}
      style={{ width: '100%', height: '100%' }}
    />
  );
}
